import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { PendingOwnershipAdminTile } from "@/pages/AdminCreators";

const STATUS_LABELS = {
    draft_pending: "Draft pending",
    draft_ready: "Draft ready",
    published: "Published",
    published_not_verified: "Published, not verified",
    live_verified: "Live verified",
    live_not_indexed: "Live, not indexed",
    needs_redo: "Needs redo",
};
const STATUSES = [
    "draft_pending",
    "draft_ready",
    "published",
    "published_not_verified",
    "live_verified",
    "live_not_indexed",
    "needs_redo",
];

export default function AdminQueue() {
    const { request } = useApi();
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [channels, setChannels] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [creatorFilter, setCreatorFilter] = useState("");
    const [openTaskId, setOpenTaskId] = useState(null);
    const [forbidden, setForbidden] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (creatorFilter) params.set("creator_id", creatorFilter);
            const url = `/admin/placement/queue${
                params.toString() ? "?" + params.toString() : ""
            }`;
            const res = await request(url);
            setTasks(res?.tasks || []);
            setChannels(res?.channels || []);
            setForbidden(false);
        } catch (err) {
            if (err?.status === 403) setForbidden(true);
            else toast.error(err?.message || "Could not load queue.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, creatorFilter]);

    // Auto-poll while any task is generating so the list fills in without reload.
    useEffect(() => {
        const anyRunning = tasks.some(
            (t) => t.generation_status === "running"
        );
        if (!anyRunning) return;
        const h = setTimeout(() => {
            void load();
        }, 4000);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasks]);

    const creators = useMemo(() => {
        const map = new Map();
        for (const t of tasks) {
            if (t.creator?.id && !map.has(t.creator.id)) {
                map.set(t.creator.id, t.creator);
            }
        }
        return Array.from(map.values());
    }, [tasks]);

    if (forbidden) {
        return (
            <div className="min-h-screen bg-paper text-ink flex items-center justify-center">
                <div data-testid="admin-queue-forbidden" className="text-sm text-black/60">
                    Admins only.
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper text-ink">
            <header className="px-5 sm:px-10 pt-6 pb-4 flex items-center justify-between">
                <Link to="/" className="inline-flex items-center">
                    <Wordmark className="h-6" />
                </Link>
                <Link
                    to="/profile"
                    data-testid="admin-queue-back"
                    className="samoony-pill samoony-pill-ghost"
                >
                    Back to profile
                </Link>
            </header>
            <main className="px-5 sm:px-10 pb-16" data-testid="admin-queue-page">
                <PendingOwnershipAdminTile testid="admin-queue-pending-ownership" />
                <h1 className="font-display text-2xl sm:text-3xl font-bold mb-4">
                    Placement queue
                </h1>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                    <label className="samoony-label flex items-center gap-2">
                        Status
                        <select
                            data-testid="queue-filter-status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="samoony-input text-xs"
                        >
                            <option value="">All</option>
                            {STATUSES.map((s) => (
                                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                            ))}
                        </select>
                    </label>
                    <label className="samoony-label flex items-center gap-2">
                        Creator
                        <select
                            data-testid="queue-filter-creator"
                            value={creatorFilter}
                            onChange={(e) => setCreatorFilter(e.target.value)}
                            className="samoony-input text-xs"
                        >
                            <option value="">All</option>
                            {creators.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name || c.samoony_handle || c.id}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button
                        data-testid="queue-refresh"
                        onClick={load}
                        className="samoony-pill samoony-pill-ghost"
                    >
                        Refresh
                    </button>
                </div>

                {loading && <div className="text-sm text-black/60">Loading...</div>}
                {!loading && tasks.length === 0 && (
                    <div className="text-sm text-black/60" data-testid="queue-empty">
                        No tasks match these filters.
                    </div>
                )}
                {!loading && tasks.length > 0 && (
                    <ul
                        data-testid="queue-list"
                        className="bg-white border border-black/10 rounded-2xl divide-y divide-black/10"
                    >
                        {tasks.map((t) => (
                            <TaskRow
                                key={t.id}
                                t={t}
                                channels={channels}
                                isOpen={openTaskId === t.id}
                                onToggle={() =>
                                    setOpenTaskId(openTaskId === t.id ? null : t.id)
                                }
                                onChanged={load}
                                showCreatorLink={true}
                            />
                        ))}
                    </ul>
                )}
            </main>
        </div>
    );
}

export function TaskRow({ t, channels, isOpen, onToggle, onChanged, showCreatorLink = false }) {
    return (
        <li className="p-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 w-full">
                    <div className="font-display text-sm">
                        {t.creator?.id ? (
                            <Link
                                data-testid={`queue-row-creator-${t.id}`}
                                to={`/admin/creators/${t.creator.id}`}
                                className="underline underline-offset-4 decoration-black/20 hover:decoration-ink"
                            >
                                {t.creator?.name || "—"}
                            </Link>
                        ) : (
                            <>{t.creator?.name || "—"}</>
                        )}{" "}
                        <span className="font-mono text-[11px] text-black/60">
                            @{t.creator?.samoony_handle}
                            {t.creator?.is_demo ? " · demo" : ""}
                        </span>
                    </div>
                    <div className="font-mono text-[11px] text-black/60 break-words">
                        {t.channel_label} · {STATUS_LABELS[t.status] || t.status}
                        {t.status === "live_verified" && (
                            <span
                                data-testid={`queue-index-indexable-${t.id}`}
                                className="ml-1"
                            >
                                · indexable
                            </span>
                        )}
                        {t.status === "live_not_indexed" && (
                            <span
                                data-testid={`queue-index-noindex-${t.id}`}
                                className="ml-1"
                            >
                                · noindex
                            </span>
                        )}
                        {t.generation_status === "running"
                            ? " · generating..."
                            : t.generation_status === "error"
                            ? " · generation error"
                            : ""}
                        {["live_verified", "live_not_indexed"].includes(t.status)
                            ? ""
                            : (
                                <>
                                    {" · publish by "}{t.suggested_publish_date}
                                </>
                            )}
                        {" · updated "}
                        {new Date(t.updated_at).toLocaleString("en-US", {
                            timeZone: "America/Edmonton",
                        })}
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:flex sm:items-center gap-2 w-full sm:w-auto">
                    {t.live_url && (
                        <a
                            href={t.live_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-[11px] underline break-all"
                        >
                            {t.live_url}
                        </a>
                    )}
                    {showCreatorLink && t.creator?.id && (
                        <Link
                            data-testid={`queue-row-admin-${t.id}`}
                            to={`/admin/creators/${t.creator.id}`}
                            className="font-mono text-[11px] underline text-black/50 hover:text-ink"
                        >
                            Open admin ↗
                        </Link>
                    )}
                    <button
                        data-testid={`queue-open-${t.id}`}
                        onClick={onToggle}
                        className="samoony-pill samoony-pill-ghost w-full sm:w-auto"
                    >
                        {isOpen ? "Close" : "Open"}
                    </button>
                </div>
            </div>
            {isOpen && (
                <TaskDetail
                    taskId={t.id}
                    channels={channels}
                    onChanged={onChanged}
                />
            )}
        </li>
    );
}

function copyText(text, label) {
    if (!text) {
        toast.error(`Nothing to copy for ${label}.`);
        return;
    }
    try {
        navigator.clipboard?.writeText(text);
        toast.success(`${label} copied.`);
    } catch {
        toast.error("Clipboard unavailable.");
    }
}

function escapeHtml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function renderInline(text) {
    let s = escapeHtml(text);
    // Links: [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2">$1</a>');
    // Bold: **x** or __x__
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/__([^_]+)__/g, "<strong>$1</strong>");
    // Italic: *x* or _x_
    s = s.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s)!.?,:;]|$)/g, "$1<em>$2</em>");
    s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s)!.?,:;]|$)/g, "$1<em>$2</em>");
    return s;
}

function markdownToHtml(md) {
    const lines = String(md || "").split("\n");
    const out = [];
    let i = 0;
    let inList = false;
    const closeList = () => {
        if (inList) {
            out.push("</ul>");
            inList = false;
        }
    };
    while (i < lines.length) {
        const ln = lines[i];
        const stripped = ln.trim();
        if (!stripped) {
            closeList();
            i++;
            continue;
        }
        const h = stripped.match(/^(#{1,6})\s+(.*)$/);
        if (h) {
            closeList();
            const level = h[1].length;
            out.push(`<h${level}>${renderInline(h[2])}</h${level}>`);
            i++;
            continue;
        }
        if (/^[-*]\s+/.test(stripped)) {
            if (!inList) {
                out.push("<ul>");
                inList = true;
            }
            out.push(`<li>${renderInline(stripped.replace(/^[-*]\s+/, ""))}</li>`);
            i++;
            continue;
        }
        // Paragraph: group non-empty, non-heading, non-list lines.
        closeList();
        const paraLines = [];
        while (
            i < lines.length &&
            lines[i].trim() &&
            !/^(#{1,6})\s+/.test(lines[i].trim()) &&
            !/^[-*]\s+/.test(lines[i].trim())
        ) {
            paraLines.push(lines[i].trim());
            i++;
        }
        out.push(`<p>${renderInline(paraLines.join(" "))}</p>`);
    }
    closeList();
    return out.join("\n");
}

async function copyFormatted(md, label) {
    const trimmed = String(md || "").trim();
    if (!trimmed) {
        toast.error(`Nothing to copy for ${label}.`);
        return;
    }
    const html = markdownToHtml(trimmed);
    try {
        if (
            navigator.clipboard &&
            window.ClipboardItem &&
            navigator.clipboard.write
        ) {
            const item = new window.ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([trimmed], { type: "text/plain" }),
            });
            await navigator.clipboard.write([item]);
            toast.success(`${label} copied.`);
            return;
        }
    } catch (err) {
        // Fall through to plain-text fallback.
    }
    try {
        await navigator.clipboard?.writeText(trimmed);
        toast.success(`${label} copied as plain text (rich copy unsupported).`);
    } catch {
        toast.error("Clipboard unavailable.");
    }
}

export function TaskDetail({ taskId, onChanged }) {
    const { request } = useApi();
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [title, setTitle] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [bodyMd, setBodyMd] = useState("");
    const [tagStr, setTagStr] = useState("");
    const [formFields, setFormFields] = useState({});
    const [liveUrl, setLiveUrl] = useState("");
    const [checklist, setChecklist] = useState([]);

    const load = async () => {
        try {
            const res = await request(`/admin/placement/task/${taskId}`);
            setDetail(res || null);
            const c = res?.task?.content || {};
            setTitle(c.title || "");
            setSubtitle(c.subtitle || "");
            setBodyMd(c.body_md || "");
            setTagStr((c.tags || []).join(", "));
            setFormFields(c.form || {});
            setLiveUrl(res?.task?.live_url || "");
            setChecklist(res?.task?.checklist || []);
        } catch (err) {
            toast.error(err?.message || "Could not load task.");
        }
    };

    useEffect(() => {
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId]);

    // Poll while a generation is in flight so the panel fills in without manual reload.
    useEffect(() => {
        const gs = detail?.task?.generation_status;
        if (gs !== "running") return;
        const h = setTimeout(() => {
            void load();
        }, 3000);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail?.task?.generation_status, detail?.task?.updated_at]);

    if (!detail) {
        return (
            <div data-testid={`task-detail-${taskId}`} className="mt-2 text-sm text-black/60">
                Loading task...
            </div>
        );
    }
    const task = detail.task;
    const channel = detail.channel;
    const creator = detail.creator || {};
    const content = task.content || {};
    const isLive = ["published", "live_verified", "live_not_indexed"].includes(
        task.status
    );

    const regenerate = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await request(`/admin/placement/task/${taskId}/regenerate`, {
                method: "POST",
            });
            toast.success("Generation started.");
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Could not start generation.");
        } finally {
            setBusy(false);
        }
    };

    const saveLiveTitle = async () => {
        if (busy) return;
        const t = (title || "").trim();
        if (!t) {
            toast.error("Title cannot be empty.");
            return;
        }
        setBusy(true);
        try {
            const res = await request(
                `/admin/placement/task/${taskId}/title`,
                { method: "POST", body: { title: t } },
            );
            toast.success("Title saved.");
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Could not save title.");
        } finally {
            setBusy(false);
        }
    };

    const save = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const tags = tagStr
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            const payload = {
                title,
                subtitle,
                body_md: bodyMd,
                tags,
                form: channel?.kind === "form" ? formFields : content.form,
                checklist,
            };
            const res = await request(`/admin/placement/task/${taskId}/edit`, {
                method: "POST",
                body: payload,
            });
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            toast.success("Saved.");
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (status) => {
        setBusy(true);
        try {
            const res = await request(`/admin/placement/task/${taskId}/status`, {
                method: "POST",
                body: { status },
            });
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            toast.success(`Status: ${STATUS_LABELS[status] || status}`);
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Status update failed.");
        } finally {
            setBusy(false);
        }
    };

    const publish = async () => {
        if (!liveUrl.trim()) {
            toast.error("Paste a URL first.");
            return;
        }
        setBusy(true);
        try {
            const res = await request(`/admin/placement/task/${taskId}/publish`, {
                method: "POST",
                body: {
                    live_url: liveUrl.trim(),
                    checklist,
                },
            });
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            if (res.verify?.ok && res.verify?.indexable !== false) {
                toast.success("Live verified.");
            } else if (res.verify?.ok) {
                toast.message(
                    `Live, not indexed: ${res.verify?.index_reason || "noindex directive"}.`
                );
            } else {
                toast.message(
                    `Published, not verified: ${res.verify?.reason || "fetch failed"}. Retry with Recheck or Mark live manually.`
                );
            }
            onChanged?.();
        } catch (err) {
            const msg = err?.message || "Publish failed.";
            if (err?.detail?.missing && Array.isArray(err.detail.missing)) {
                toast.error(
                    `${msg} Missing: ${err.detail.missing.slice(0, 3).join("; ")}` +
                        (err.detail.missing.length > 3 ? "…" : "")
                );
            } else {
                toast.error(msg);
            }
        } finally {
            setBusy(false);
        }
    };

    const markLive = async (indexable) => {
        if (!liveUrl.trim()) {
            toast.error("Paste a URL first.");
            return;
        }
        setBusy(true);
        try {
            const res = await request(
                `/admin/placement/task/${taskId}/mark-live`,
                {
                    method: "POST",
                    body: {
                        live_url: liveUrl.trim(),
                        indexable: !!indexable,
                    },
                }
            );
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            toast.success(
                indexable ? "Marked Live verified." : "Marked Live, not indexed."
            );
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Mark live failed.");
        } finally {
            setBusy(false);
        }
    };

    const recheck = async () => {
        setBusy(true);
        try {
            const res = await request(`/admin/placement/task/${taskId}/recheck`, {
                method: "POST",
            });
            setDetail((prev) => ({ ...(prev || {}), task: res.task }));
            if (res.verify?.ok && res.verify?.indexable !== false) {
                toast.success("Live verified.");
            } else if (res.verify?.ok) {
                toast.message(
                    `Live, not indexed: ${res.verify?.index_reason || "noindex directive"}.`
                );
            } else {
                toast.message(`Recheck: ${res.verify?.reason || "failed"}.`);
            }
            onChanged?.();
        } catch (err) {
            toast.error(err?.message || "Recheck failed.");
        } finally {
            setBusy(false);
        }
    };

    const toggleCheck = (key) => {
        setChecklist((prev) =>
            prev.map((item) => (item.key === key ? { ...item, done: !item.done } : item))
        );
    };

    return (
        <div
            data-testid={`task-detail-${taskId}`}
            className="mt-3 bg-paper border border-black/10 rounded-xl p-3 space-y-3"
        >
            <div className="font-mono text-[11px] text-black/60">
                {creator.name} · @{creator.instagram_handle} · {creator.city}
                {creator.is_demo ? " · demo" : ""} · channel: {channel?.label}
            </div>

            {task.generation_status === "running" && (
                <div
                    data-testid={`task-generation-running-${taskId}`}
                    className="font-mono text-[11px] text-signal border border-signal/40 rounded-md px-2 py-1 bg-signal/5"
                >
                    Generating...
                </div>
            )}
            {task.generation_status === "error" && task.generation_error && (
                <div
                    data-testid={`task-generation-error-${taskId}`}
                    className="font-mono text-[11px] text-red-700 border border-red-300 rounded-md px-2 py-1 bg-red-50 whitespace-pre-wrap"
                >
                    Generation error: {task.generation_error}
                </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
                {!isLive && (
                    <button
                        data-testid={`task-regenerate-${taskId}`}
                        onClick={regenerate}
                        disabled={busy || task.generation_status === "running"}
                        className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                    >
                        {task.generation_status === "running"
                            ? "Generating..."
                            : busy
                            ? "Starting..."
                            : "Regenerate"}
                    </button>
                )}
                {isLive ? (
                    <button
                        data-testid={`task-save-live-title-${taskId}`}
                        onClick={saveLiveTitle}
                        disabled={busy}
                        className="samoony-pill disabled:opacity-30 w-full sm:w-auto"
                    >
                        Save title
                    </button>
                ) : (
                    <button
                        data-testid={`task-save-${taskId}`}
                        onClick={save}
                        disabled={busy}
                        className="samoony-pill disabled:opacity-30 w-full sm:w-auto"
                    >
                        Save
                    </button>
                )}
                {!isLive && (
                    <button
                        data-testid={`task-status-draft-ready-${taskId}`}
                        onClick={() => setStatus("draft_ready")}
                        disabled={busy}
                        className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                    >
                        Mark draft ready
                    </button>
                )}
                <button
                    data-testid={`task-status-needs-redo-${taskId}`}
                    onClick={() => setStatus("needs_redo")}
                    disabled={busy}
                    className="samoony-pill samoony-pill-ghost disabled:opacity-30 w-full sm:w-auto"
                >
                    Needs redo
                </button>
            </div>

            {channel?.kind === "form" ? (
                <div className="space-y-2">
                    <FieldWithCopy
                        label="Title"
                        value={title}
                        onChange={setTitle}
                        testid={`task-title-${taskId}`}
                    />
                    <button
                        data-testid={`task-save-title-${taskId}`}
                        onClick={saveLiveTitle}
                        disabled={busy}
                        className="samoony-pill disabled:opacity-30 w-full sm:w-auto"
                    >
                        Save title
                    </button>
                    {(() => {
                        const sf = (detail?.creator && detail.creator.suggested_form) || {};
                        return (
                            <>
                                <FormField
                                    label="Name"
                                    testid={`form-name-${taskId}`}
                                    value={formFields.name || sf.name || ""}
                                    onChange={(v) => setFormFields({ ...formFields, name: v })}
                                />
                                <FormField
                                    label="Instagram handle"
                                    testid={`form-handle-${taskId}`}
                                    value={formFields.instagram_handle || sf.instagram_handle || ""}
                                    onChange={(v) =>
                                        setFormFields({ ...formFields, instagram_handle: v })
                                    }
                                />
                                <FormField
                                    label="Category"
                                    testid={`form-category-${taskId}`}
                                    value={formFields.category || sf.category || ""}
                                    onChange={(v) => setFormFields({ ...formFields, category: v })}
                                />
                                <FormField
                                    label="City"
                                    testid={`form-city-${taskId}`}
                                    value={formFields.city || sf.city || ""}
                                    onChange={(v) => setFormFields({ ...formFields, city: v })}
                                />
                                <FormField
                                    label={`Bio (${(formFields.bio || sf.bio || "").length}/150)`}
                                    testid={`form-bio-${taskId}`}
                                    value={formFields.bio || sf.bio || ""}
                                    onChange={(v) =>
                                        setFormFields({ ...formFields, bio: v.slice(0, 150) })
                                    }
                                    textarea
                                />
                            </>
                        );
                    })()}
                </div>
            ) : (
                <>
                    <FieldWithCopy
                        label="Title"
                        value={title}
                        onChange={setTitle}
                        testid={`task-title-${taskId}`}
                    />
                    {(channel?.id === "medium" || channel?.id === "substack") && (
                        <FieldWithCopy
                            label="Subtitle"
                            value={subtitle}
                            onChange={setSubtitle}
                            testid={`task-subtitle-${taskId}`}
                        />
                    )}
                    <div
                        data-testid={`task-meta-${taskId}`}
                        className="font-mono text-[11px] text-black/60 space-y-1"
                    >
                        <div>
                            {(() => {
                                const wc = Number(content.word_count || 0) ||
                                    (bodyMd ? bodyMd.trim().split(/\s+/).filter(Boolean).length : 0);
                                const fmt = content.format_label || content.format_id || detail?.task?.format_id;
                                const topic = content.topic_label || detail?.task?.topic_label;
                                const reader = content.reader_label || detail?.task?.reader_label;
                                return (
                                    <>
                                        <span data-testid={`task-word-count-${taskId}`}>
                                            {wc} word{wc === 1 ? "" : "s"}
                                        </span>
                                        {fmt && (
                                            <span data-testid={`task-format-${taskId}`}>
                                                {" · format: "}{fmt}
                                            </span>
                                        )}
                                        {topic && (
                                            <span data-testid={`task-topic-${taskId}`}>
                                                {" · topic: "}{topic}
                                            </span>
                                        )}
                                        {reader && (
                                            <span data-testid={`task-reader-${taskId}`}>
                                                {" · reader: "}{reader}
                                            </span>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        {content.similarity_check && (
                            <div
                                data-testid={`task-similarity-${taskId}`}
                                className={
                                    content.similarity_check.ok
                                        ? "text-black/60"
                                        : "text-[color:var(--signal)]"
                                }
                            >
                                {content.similarity_check.ok
                                    ? "similarity check passed"
                                    : "similarity check: "}
                                {!content.similarity_check.ok && content.similarity_check.reason}
                                {content.similarity_check.ok && content.similarity_check.tries > 0 &&
                                    ` (regenerated ${content.similarity_check.tries} time${content.similarity_check.tries === 1 ? "" : "s"})`}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="samoony-label">Body (markdown)</div>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    data-testid={`task-body-md-${taskId}-copy`}
                                    onClick={() =>
                                        copyText(bodyMd || "", "Body (markdown)")
                                    }
                                    className="samoony-pill samoony-pill-ghost text-xs"
                                >
                                    Copy
                                </button>
                                <button
                                    data-testid={`task-copy-body-formatted-${taskId}`}
                                    onClick={() =>
                                        copyFormatted(bodyMd || "", "Formatted body")
                                    }
                                    className="samoony-pill samoony-pill-ghost text-xs"
                                >
                                    Copy formatted
                                </button>
                            </div>
                        </div>
                        <textarea
                            data-testid={`task-body-md-${taskId}`}
                            value={bodyMd || ""}
                            onChange={(e) => setBodyMd(e.target.value)}
                            rows={10}
                            className="samoony-input w-full font-mono text-xs"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="samoony-label">Body (plain text)</div>
                        <button
                            data-testid={`task-copy-body-plain-${taskId}`}
                            onClick={() => copyText(content.body_plain || "", "Plain body")}
                            className="samoony-pill samoony-pill-ghost text-xs"
                        >
                            Copy plain
                        </button>
                    </div>
                    <textarea
                        readOnly
                        value={content.body_plain || ""}
                        rows={4}
                        className="samoony-input w-full font-mono text-xs"
                    />
                    <FieldWithCopy
                        label="Tags"
                        value={tagStr}
                        onChange={setTagStr}
                        testid={`task-tags-${taskId}`}
                    />
                </>
            )}

            <div>
                <div className="samoony-label mb-1">Links</div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid={`task-copy-samoony-${taskId}`}
                        onClick={() =>
                            copyText(content.links?.samoony || "", "Samoony URL")
                        }
                        className="samoony-pill samoony-pill-ghost text-xs"
                    >
                        Copy Samoony URL
                    </button>
                    <span className="font-mono text-[11px] break-all">
                        {content.links?.samoony || "—"}
                    </span>
                    {content.links?.samoony && content.links?.samoony_live === false && (
                        <span
                            data-testid={`task-samoony-not-live-${taskId}`}
                            className="font-mono text-[11px] text-black/60 italic"
                        >
                            not live yet
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                    <button
                        data-testid={`task-copy-instagram-${taskId}`}
                        onClick={() =>
                            copyText(content.links?.instagram || "", "Instagram URL")
                        }
                        className="samoony-pill samoony-pill-ghost text-xs"
                    >
                        Copy Instagram URL
                    </button>
                    <span className="font-mono text-[11px] break-all">
                        {content.links?.instagram || "—"}
                    </span>
                </div>
            </div>

            <div>
                <div className="samoony-label mb-1">Quality checks</div>
                <ul className="space-y-1">
                    {checklist
                        .filter((item) => !String(item.key || "").startsWith("publishing:"))
                        .map((item) => (
                            <li key={item.key} className="flex items-center gap-2 text-xs">
                                <input
                                    type="checkbox"
                                    data-testid={`check-${item.key}-${taskId}`}
                                    checked={!!item.done}
                                    onChange={() => toggleCheck(item.key)}
                                />
                                <span>{item.label}</span>
                            </li>
                        ))}
                </ul>
            </div>

            <div className="border-t border-black/10 pt-2">
                <div className="samoony-label mb-1">Publishing steps</div>
                <ul className="space-y-1" data-testid={`task-publishing-steps-${taskId}`}>
                    {checklist
                        .filter((item) => String(item.key || "").startsWith("publishing:"))
                        .map((item) => (
                            <li key={item.key} className="flex items-center gap-2 text-xs">
                                <input
                                    type="checkbox"
                                    data-testid={`check-${item.key}-${taskId}`}
                                    checked={!!item.done}
                                    onChange={() => toggleCheck(item.key)}
                                />
                                <span>{item.label}</span>
                            </li>
                        ))}
                </ul>
            </div>

            <div className="border-t border-black/10 pt-2">
                <div className="samoony-label mb-1">Publish</div>
                {content.links?.samoony_live === false && (
                    <div
                        data-testid={`task-publish-blocked-${taskId}`}
                        className="font-mono text-[11px] text-black/60 italic mb-1"
                    >
                        Publish after the page is live
                    </div>
                )}
                {(() => {
                    const steps = checklist.filter((it) =>
                        String(it.key || "").startsWith("publishing:")
                    );
                    const incomplete = steps.filter((it) => !it.done);
                    const stepsDone = incomplete.length === 0;
                    const pageLive = content.links?.samoony_live !== false;
                    const canPublish = stepsDone && pageLive && !busy;
                    return (
                        <>
                            {!stepsDone && (
                                <div
                                    data-testid={`task-publish-steps-pending-${taskId}`}
                                    className="font-mono text-[11px] text-black/60 italic mb-1"
                                >
                                    Tick every publishing step before publishing (
                                    {incomplete.length} remaining).
                                </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    data-testid={`task-live-url-${taskId}`}
                                    value={liveUrl}
                                    onChange={(e) => setLiveUrl(e.target.value)}
                                    placeholder="Paste live URL"
                                    disabled={!pageLive}
                                    className="samoony-input flex-1 text-xs font-mono min-w-[200px] disabled:opacity-40"
                                />
                                <button
                                    data-testid={`task-publish-${taskId}`}
                                    onClick={publish}
                                    disabled={!canPublish}
                                    className="samoony-pill disabled:opacity-30"
                                >
                                    {busy ? "Verifying..." : "Publish and verify"}
                                </button>
                                <button
                                    data-testid={`task-recheck-${taskId}`}
                                    onClick={recheck}
                                    disabled={busy || !task.live_url}
                                    className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                                >
                                    {busy ? "Checking..." : "Check now"}
                                </button>
                            </div>
                        </>
                    );
                })()}
                {task.verify_log && (
                    <div
                        data-testid={`task-verify-log-${taskId}`}
                        className="font-mono text-[11px] text-black/60 mt-1"
                    >
                        {task.verify_log.ok ? "Handle present" : "Handle not found"}
                        {task.verify_log.reason ? ` · ${task.verify_log.reason}` : ""}
                        {task.verify_log.indexable === true
                            ? " · indexable"
                            : task.verify_log.indexable === false
                            ? ` · noindex (${task.verify_log.index_reason || "robots directive"})`
                            : ""}
                        {task.last_checked_at
                            ? ` · checked ${new Date(task.last_checked_at).toLocaleString("en-US", { timeZone: "America/Edmonton" })}`
                            : ""}
                    </div>
                )}
                {task.manual_verified_at && (
                    <div
                        data-testid={`task-manual-verified-${taskId}`}
                        className="font-mono text-[11px] text-black/60 mt-1"
                    >
                        Marked live manually ·
                        {" "}
                        {new Date(task.manual_verified_at).toLocaleString(
                            "en-US",
                            { timeZone: "America/Edmonton" }
                        )}
                    </div>
                )}
                {task.check_result === "blocked" && (
                    <div
                        data-testid={`task-blocked-${taskId}`}
                        className="font-mono text-[11px] mt-1 px-2 py-1 rounded"
                        style={{
                            background: "var(--color-growing-bg)",
                            color: "var(--color-growing-fg)",
                        }}
                    >
                        This site blocks automatic checks
                        {task.check_status_code
                            ? ` (HTTP ${task.check_status_code})`
                            : ""}
                        . Check it by hand.
                        {task.last_checked_at
                            ? ` Last attempt: ${new Date(task.last_checked_at).toLocaleString("en-US", { timeZone: "America/Edmonton" })}.`
                            : ""}
                    </div>
                )}
                <div
                    data-testid={`task-recheck-schedule-${taskId}`}
                    className="font-mono text-[11px] text-black/50 italic mt-1"
                >
                    Live tasks are rechecked automatically once a day at 3am Edmonton time, and whenever you press Check now.
                </div>
            </div>

            <div className="border-t border-black/10 pt-2">
                <div className="samoony-label mb-1">Mark live manually</div>
                <div className="font-mono text-[11px] text-black/60 italic mb-1">
                    Use when the host blocks fetching but the article is live.
                    Sets the same state as a verified result.
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid={`task-mark-live-indexable-${taskId}`}
                        onClick={() => markLive(true)}
                        disabled={busy || content.links?.samoony_live === false || !liveUrl.trim()}
                        className="samoony-pill samoony-pill-ghost disabled:opacity-30"
                    >
                        Mark Live verified
                    </button>
                </div>
            </div>
        </div>
    );
}

function FieldWithCopy({ label, value, onChange, testid, textarea }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <div className="samoony-label">{label}</div>
                <button
                    data-testid={`${testid}-copy`}
                    onClick={() => copyText(value || "", label)}
                    className="samoony-pill samoony-pill-ghost text-xs"
                >
                    Copy
                </button>
            </div>
            {textarea ? (
                <textarea
                    data-testid={testid}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={10}
                    className="samoony-input w-full font-mono text-xs"
                />
            ) : (
                <input
                    data-testid={testid}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="samoony-input w-full text-sm"
                />
            )}
        </div>
    );
}

function FormField({ label, value, onChange, testid, textarea }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <div className="samoony-label">{label}</div>
                <button
                    data-testid={`${testid}-copy`}
                    onClick={() => copyText(value || "", label)}
                    className="samoony-pill samoony-pill-ghost text-xs"
                >
                    Copy
                </button>
            </div>
            {textarea ? (
                <textarea
                    data-testid={testid}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    className="samoony-input w-full text-xs"
                />
            ) : (
                <input
                    data-testid={testid}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="samoony-input w-full text-sm"
                />
            )}
        </div>
    );
}
